import _ from 'lodash'
import moment from 'moment'
import React from 'react'
import { Link } from 'react-router'

import { NEUTRAL_COLOURS } from 'constants/colors'
import { DAYS_OF_WEEK_LIST } from 'constants/date'
import {
  ChildBooking,
  ChildProductRegularAmountDiscount,
  ChildProductRegularPercentageDiscount,
} from 'services/booking/childBooking/constants'
import { ChildProductTypes } from 'services/booking/childProducts/constants'

import { generateRoute } from 'utils/routing'

import { Avatar, Box, Button, Grid, Space, Tabs, Typography } from 'components'
import {
  CHILD_BOOKING_QUERY_NAME,
} from 'module/Children/Child/ChildBookingPattern/RegularBookings/RegularBookingsAdd/RegularBookingsAddContainer'

import SubdomainCurrencyProvider from 'providers/SubdomainCurrencyProvider'
import i18n from 'translations'

import { StyledButtonWrapper } from '../RegularBookingsListStyled'

interface RegularBookingsListItemProps {
  childBooking: ChildBooking
  editButtonComponent?: React.ReactNode
  hasAccessToEditRegularBooking: boolean
  isChildContext: boolean
}

const RegularBookingsListItem: React.FC<RegularBookingsListItemProps> = ({
  childBooking,
  editButtonComponent,
  hasAccessToEditRegularBooking,
  isChildContext,
}) => {
  const { alternates, attendancePeriod, child: recordChild, endDate, id, startDate } = childBooking || {}
  const { firstName, leavingDate, photo, surname } = recordChild || {}

  const renderTab = (record: ChildBooking, alternate: number) => {
    const { childProducts } = record || {}

    const childProductsFilteredByAlternate = _.filter(childProducts, (item) => (
      alternate + 1 === item.alternate
    ))

    const sortDayOfWeeks = (items) => _.sortBy(
      _.map(items, (i) => i.dayOfWeek),
      (day) => (
        _.findIndex(DAYS_OF_WEEK_LIST, (i) => (day === i))
      ),
    )

    const regularItems = _.map(
      _.flow(
        (i) => _.groupBy(i, (item) => item.product.id),
        (groupedItems) => _.mapValues(groupedItems, (elements) => ({
          ...elements[0],
          dayOfWeek: sortDayOfWeeks(elements),
          totalQuantity: _.sumBy(elements, (element) => element.settings.quantity),
        })),
      )(
        _.filter(childProductsFilteredByAlternate, (item) => (
          ChildProductTypes.REGULAR_ITEM === item.type
        )),
      ),
    )

    const sessions = _.map(
      _.flow(
        (i) => _.groupBy(i, (item) => item.product.id),
        (groupedItems) => _.mapValues(groupedItems, (elements) => ({
          ...elements[0],
          dayOfWeek: sortDayOfWeeks(elements),
        })),
      )(
        _.filter(childProductsFilteredByAlternate, (item) => (
          ChildProductTypes.REGULAR_SESSION === item.type
        )),
      ),
    )

    const fundings = _.map(_.flow(
      (i) => _.groupBy(i, (item) => item.product.id),
      (groupedItems) => groupedItems,
    )(_.filter(childProductsFilteredByAlternate, (item) => ChildProductTypes.REGULAR_ALLOCATED_FUNDING === item.type)))

    const discounts = _.map(
      _.flow(
        (i) => _.groupBy(i, (item) => item.product.id),
        (groupedItems) => _.mapValues(groupedItems, (elements) => ({
          ...elements[0],
          dayOfWeek: sortDayOfWeeks(elements),
        })),
      )(
        _.filter(childProductsFilteredByAlternate, (item) => (
          ChildProductTypes.REGULAR_LINK_DISCOUNT === item.type
        )),
      ),
    )

    const customDiscounts = _.map(
      _.flow(
        (i) => _.groupBy(i, (item) => `${item.settings.name}_${item.settings.value}`),
        (groupedItems) => _.mapValues(groupedItems, (elements) => ({
          ...elements[0],
          dayOfWeek: sortDayOfWeeks(elements),
        })),
      )(
        _.filter(childProductsFilteredByAlternate, (item) => (
          ChildProductTypes.REGULAR_AMOUNT_DISCOUNT === item.type
          || ChildProductTypes.REGULAR_PERCENTAGE_DISCOUNT === item.type
        )),
      ),
    )

    const renderList = (items, hideEmptyState?, showQuantity?) => (
      <React.Fragment>
        <Space space="10px" />
        <Typography fontSize={14} wordBreak="break-word">
          {!hideEmptyState && !items.length ? '-' : null}
          {_.map(items, (item) => (
            <div key={item.id}>
              {showQuantity && `${item.totalQuantity || item.dayOfWeek.length}x `}
              {item.product.name}
              {' ('}
              {_.map(item.dayOfWeek, (dayOfWeek, index) => (
                <React.Fragment key={dayOfWeek}>
                  {i18n.t(`global:shortDayNames:${_.findIndex(DAYS_OF_WEEK_LIST, (i) => i === dayOfWeek) + 1}`)}
                  {index + 1 !== item.dayOfWeek.length && ', '}
                </React.Fragment>
              ))}
              {')'}
            </div>
          ))}
        </Typography>
      </React.Fragment>
    )

    const renderCustomDiscounts = () => (
      <Typography fontSize={14} wordBreak="break-word">
        {!customDiscounts.length && !discounts.length ? '-' : null}
        {_.map(customDiscounts, (item: ChildProductRegularPercentageDiscount | ChildProductRegularAmountDiscount) => (
          <SubdomainCurrencyProvider key={item?.id}>
            {({ getFormattedCurrencyValue }) => (
              <div key={item.id}>
                {`${item.dayOfWeek.length}x `}
                {item.settings.name}
                {' '}
                {item.type === ChildProductTypes.REGULAR_AMOUNT_DISCOUNT
                  ? getFormattedCurrencyValue(item.settings.value / 100)
                  : `${item.settings.value / 100}%`}
                {' ('}
                {_.map(item.dayOfWeek, (dayOfWeek, index) => (
                  <React.Fragment key={dayOfWeek}>
                    {i18n.t(`global:shortDayNames:${_.findIndex(DAYS_OF_WEEK_LIST, (i) => i === dayOfWeek) + 1}`)}
                    {index + 1 !== item.dayOfWeek.length && ', '}
                  </React.Fragment>
                ))}
                {')'}
              </div>
            )}
          </SubdomainCurrencyProvider>
        ))}
      </Typography>
    )

    const renderFunding = () => (
      <React.Fragment>
        <Space space="10px" />
        <Typography fontSize={14}>
          {!fundings.length ? '-' : null}
          {_.map(fundings, (fundingsGroup) => {
            let times = 0
            const item = fundingsGroup[0]

            _.each(fundingsGroup, (element) => {
              const { settings } = element || {}
              const { defaultAllocations } = settings || {}

              _.each(defaultAllocations, (allocation) => {
                _.each(allocation?.times, (time) => {
                  times += time.endTime - time.startTime
                })
              })
            })

            const hours = Math.floor(Math.round((times / 1000 / 60 / 60) * 100) / 100)
            const minutes = +moment(times).utc().format('m')

            return (
              <div key={item.id}>
                {hours ? `${i18n.t('global:Hours', { value: hours })}` : null}
                {minutes ? ` ${i18n.t('global:Minutes', { value: minutes })}` : null}
                {hours || minutes ? ' - ' : null}
                {item.product.name}
              </div>
            )
          })}
        </Typography>
      </React.Fragment>
    )

    return (
      <Grid spacing={20}>
        <Grid.Item flex={{ desktop: 1 }} width={{ mobile: '100%' }}>
          <Typography bold>
            {i18n.t('module:Finance:RegularBookings:List:sessions')}
          </Typography>
          {renderList(sessions, false, true)}
        </Grid.Item>
        <Grid.Item flex={{ desktop: 1 }} width={{ mobile: '100%' }}>
          <Typography bold>
            {i18n.t('module:Finance:RegularBookings:List:items')}
          </Typography>
          {renderList(regularItems, false, true)}
        </Grid.Item>
        <Grid.Item flex={{ desktop: 1 }} width={{ mobile: '100%' }}>
          <Typography bold>
            {i18n.t('module:Finance:RegularBookings:List:funding')}
          </Typography>
          <Space space="15px" />
          {renderFunding()}
        </Grid.Item>
        <Grid.Item flex={{ desktop: 1 }} width={{ mobile: '100%' }}>
          <Typography bold>
            {i18n.t('module:Finance:RegularBookings:List:discounts')}
          </Typography>
          {renderList(discounts, true, true)}
          {renderCustomDiscounts()}
        </Grid.Item>
      </Grid>
    )
  }

  return (
    <Box key={childBooking.id} withPadding withShadow>
      {!isChildContext && (
        <Grid>
          <Grid.Item flex={{ desktop: 1 }} justifyContent={{ mobile: 'center' }}>
            <Avatar avatarSize="small" initials={[firstName, surname]} src={photo} title={`${firstName} ${surname}`} />
            <Space space="5px" />
          </Grid.Item>
        </Grid>
      )}
      <Grid>
        <Grid.Item
          alignItems={{ desktop: 'flex-end' }}
          justifyContent={{ mobile: 'center' }}
          width={{ mobile: '100%' }}
        >
          {hasAccessToEditRegularBooking ? (
            <Link
              to={generateRoute('CHILDREN.CHILD.BOOKING_PATTERN.REGULAR_BOOKINGS.PREVIEW', {
                bookingId: id,
                childId: recordChild?.id,
              })}
            >
              <Typography fontSize={18} bold primary>
                {moment(startDate).format('DD MMM YYYY')}
                {' - '}
                {endDate
                  ? moment(endDate).format('DD MMM YYYY')
                  : (
                    <Typography fontSize={18} bold inline primary>
                      {moment(leavingDate).format('DD MMM YYYY')}
                      <Typography color={NEUTRAL_COLOURS.GRAY} fontSize={14} bold inline>
                        {' ('}
                        {i18n.t('module:Finance:RegularBookings:List:untilChildLeaves')}
                        {')'}
                      </Typography>
                    </Typography>
                  )}
              </Typography>
            </Link>
          ) : (
            <Typography fontSize={18} bold primary>
              {moment(startDate).format('DD MMM YYYY')}
              {' - '}
              {endDate
                ? moment(endDate).format('DD MMM YYYY')
                : (
                  <Typography fontSize={18} bold inline primary>
                    {moment(leavingDate).format('DD MMM YYYY')}
                    <Typography color={NEUTRAL_COLOURS.GRAY} fontSize={14} bold inline>
                      {' ('}
                      {i18n.t('module:Finance:RegularBookings:List:untilChildLeaves')}
                      {')'}
                    </Typography>
                  </Typography>
                )}
            </Typography>
          )}
        </Grid.Item>
        <Grid.Item
          alignItems={{ desktop: 'flex-end' }}
          flex={{ desktop: 1 }}
          textAlign={{ mobile: 'center' }}
          width={{ mobile: '100%' }}
        >
          <Typography color={NEUTRAL_COLOURS.GRAY} fontSize={14} bold>
            {attendancePeriod?.name}
          </Typography>
        </Grid.Item>
        {hasAccessToEditRegularBooking && (
          <Grid.Item justifyContent={{ desktop: 'flex-end', mobile: 'center' }} width={{ mobile: '100%' }}>
            {editButtonComponent || (
              <StyledButtonWrapper>
                <Button
                  hierarchy="secondary"
                  label={i18n.t('module:Finance:RegularBookings:List:scheduleBookingPatternChange')}
                  margin="0"
                  size="small"
                  to={`${generateRoute('CHILDREN.CHILD.BOOKING_PATTERN.REGULAR_BOOKINGS.ADD', {
                    childId: recordChild?.id,
                  })}?${CHILD_BOOKING_QUERY_NAME}=${id}`}
                />
              </StyledButtonWrapper>
            )}
          </Grid.Item>
        )}
      </Grid>
      <Space space="20px" />
      <Tabs fullWidth={false} gray noMargin>
        {_.times(alternates, (index) => (
          <Tabs.Item
            key={index}
            title={(
              <Typography fontSize={14} bold>
                {i18n.t('module:Finance:RegularBookings:List:week', { index: index + 1 })}
              </Typography>
            )}
          >
            {renderTab(childBooking, index)}
          </Tabs.Item>
        ))}
      </Tabs>
    </Box>
  )
}

export default RegularBookingsListItem
